import { Link } from "react-router-dom";
import { Fuel, Gauge, Calendar, Settings2 } from "lucide-react";
import { Badge } from "./ui/badge";

export const VehicleListItem = ({ vehicle }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatMileage = (km) => {
    return new Intl.NumberFormat('it-IT').format(km) + ' km';
  };

  const getYear = (registration) => {
    if (!registration) return 'N/A';
    return registration.split('-')[0];
  };

  const getFuelLabel = (fuel) => {
    const fuels = {
      'gasolio': 'Diesel',
      'benzina': 'Benzina',
      'metano': 'Metano',
      'gpl': 'GPL',
      'elettrico': 'Elettrico',
      'ibrido': 'Ibrido'
    };
    return fuels[fuel?.toLowerCase()] || fuel || 'N/A';
  };

  const mainImage = vehicle.images?.[0] || 'https://images.unsplash.com/photo-1655285162488-f495df383244?w=600';

  return (
    <Link 
      to={`/veicolo/${vehicle.id}`} 
      className="group block"
      data-testid={`vehicle-list-item-${vehicle.id}`}
    >
      <div className="bg-white border border-zinc-100 card-listing overflow-hidden flex flex-col md:flex-row">
        {/* Image Container */}
        <div className="relative w-full md:w-72 lg:w-80 flex-shrink-0 aspect-[4/3] md:aspect-auto md:h-48 overflow-hidden bg-zinc-200">
          <img
            src={mainImage}
            alt={`${vehicle.brand} ${vehicle.model}`}
            className="w-full h-full object-cover image-zoom"
            loading="lazy"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1655285162488-f495df383244?w=600';
            }}
          />
          
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {vehicle.status === 'disponibile' && (
              <Badge className="bg-green-600 text-white text-xs font-semibold uppercase tracking-wide px-2 py-1">
                Disponibile
              </Badge>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-5 flex flex-col justify-between">
          <div>
            {/* Brand & Model */}
            <div className="mb-3">
              <p className="text-xs text-zinc-500 font-semibold uppercase tracking-wider mb-1">
                {vehicle.brand}
              </p>
              <h3 
                className="text-xl font-bold text-zinc-900"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {vehicle.model}
              </h3>
              <p className="text-sm text-zinc-600 mt-1">
                {vehicle.version}
              </p>
            </div>

            {/* Specs Row */}
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex items-center gap-2 text-zinc-600">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">{getYear(vehicle.initial_registration)}</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-600">
                <Gauge className="w-4 h-4" />
                <span className="text-sm">{formatMileage(vehicle.mileage)}</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-600">
                <Fuel className="w-4 h-4" />
                <span className="text-sm">{getFuelLabel(vehicle.fuel_type)}</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-600">
                <Settings2 className="w-4 h-4" />
                <span className="text-sm capitalize">{vehicle.transmission === 'manual' ? 'Manuale' : 'Automatico'}</span>
              </div>
            </div>
          </div>

          {/* Price */}
          <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
            <p 
              className="text-2xl font-bold text-blue-600"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {formatPrice(vehicle.price)}
            </p>
            <span className="text-xs text-zinc-500 uppercase tracking-wide">
              IVA incl.
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default VehicleListItem;
